import { api } from './client'
import { version as APP_VERSION } from '../../package.json'

// C14 product events. An in-memory queue flushed at 20 items, when the page
// goes hidden, and every 30s. A failed POST drops the batch — telemetry never
// retries and never blocks anything the user asked for.

const INSTALL_KEY = 'navoption.install_id'
const FLUSH_AT = 20
const FLUSH_MS = 30_000

export type EventTier = 'free' | 'pro' | 'unknown'

// Payload keys are the contract: these four events carry exactly these fields.
type EventPayloads = {
  scan_run: { side: 'put' | 'call'; result_count: number; truncated: boolean }
  scan_result_opened: { symbol: string; side: 'put' | 'call'; rank_position: number }
  symbol_detail_opened: { symbol: string; entry_point: 'find' | 'today' | 'portfolio' | 'market' | 'deep_link' | 'search' }
  analyze_started: { symbol: string; requested_mode: 'fast' | 'deep'; entry_point: 'symbol_detail' | 'earnings_week' | 'portfolio' }
}

type EventName = keyof EventPayloads

type Envelope = {
  event: EventName
  event_version: 1
  occurred_at: string
  session_id: string
  install_id: string | null
  tier: EventTier
  source: 'web'
  app_version: string
  build: string
  payload: EventPayloads[EventName]
}

// One session per page load; the install id is the stable browser identity.
const sessionId = crypto.randomUUID()

const installId = (() => {
  try {
    const saved = localStorage.getItem(INSTALL_KEY)
    if (saved) return saved
    const fresh = crypto.randomUUID()
    localStorage.setItem(INSTALL_KEY, fresh)
    return fresh
  } catch {
    return null
  }
})()

// Stays 'unknown' until /v1/me answers; a signed-out visitor has no tier.
let tier: EventTier = 'unknown'

export function setEventTier(next: EventTier) {
  tier = next
}

let queue: Envelope[] = []

// The queue flushes at 20, so it never reaches the server's 50-per-request cap.
export function flushEvents() {
  if (queue.length === 0) return
  const batch = queue
  queue = []
  api('/v1/events', { method: 'POST', body: JSON.stringify({ events: batch }) }).catch(() => { /* dropped */ })
}

export function track<E extends EventName>(event: E, payload: EventPayloads[E]) {
  queue.push({
    event,
    event_version: 1,
    occurred_at: new Date().toISOString(),
    session_id: sessionId,
    install_id: installId,
    tier,
    source: 'web',
    app_version: APP_VERSION,
    build: 'web',
    payload,
  })
  if (queue.length >= FLUSH_AT) flushEvents()
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushEvents()
})
window.setInterval(flushEvents, FLUSH_MS)
